/**
 * Admin · Barcodes & bins (§29 / §B-8). Two tabs: SKU↔barcode aliases and bin
 * locations. MANAGER sees read-only searchable tables; ADMIN adds / edits /
 * deletes and bulk-imports from pasted CSV. Writes go through the ADMIN API
 * (audited, optimistic concurrency).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api, type AdminSkuBarcode, type AdminSkuLocation, type SkuBarcodeInput, type SkuLocationInput, type ImportResult, type ImportSummary } from "../../lib/api.js";
import { parseCsv, csvBool } from "../../lib/csv.js";
import { useSession } from "../../components/auth.js";
import { Tabs, type TabItem, Badge, StateChip, DataTable, type Column, Drawer, Field, Input, SearchBar, Button, EmptyState, ErrorState, SkeletonRows, useToast } from "../../components/ui/index.js";

type Tab = "aliases" | "bins";
const TABS: TabItem[] = [
  { id: "aliases", label: "Aliases" },
  { id: "bins", label: "Bin locations" },
];

export function Barcodes() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("aliases");

  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Barcodes &amp; bins</h1>
        </div>
        {!isAdmin && <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>}
      </div>
      <Tabs items={TABS} value={tab} onChange={(id) => setTab(id as Tab)} ariaLabel="Barcode section" />
      {tab === "aliases" ? <AliasesPane isAdmin={isAdmin} toast={toast} /> : <BinsPane isAdmin={isAdmin} toast={toast} />}
    </section>
  );
}

type Toast = ReturnType<typeof useToast>["toast"];

/* ----------------------------- Aliases ----------------------------- */
function AliasesPane({ isAdmin, toast }: { isAdmin: boolean; toast: Toast }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ aliases: AdminSkuBarcode[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState<AdminSkuBarcode | null | "new">(null);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await api.admin.skuBarcodes({ q, page, limit }));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [q, page]);
  useEffect(() => {
    void load();
  }, [load]);

  const remove = useCallback(
    async (a: AdminSkuBarcode) => {
      if (!window.confirm(`Delete alias ${a.sku} → ${a.barcode}?`)) return;
      setBusy(true);
      try {
        await api.admin.deleteSkuBarcode(a.id);
        toast("Alias deleted", "success");
        void load();
      } catch {
        toast("Couldn’t delete.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [load, toast],
  );

  const columns = useMemo<Array<Column<AdminSkuBarcode>>>(() => {
    const base: Array<Column<AdminSkuBarcode>> = [
      { key: "sku", header: "SKU", cell: (a) => <span className="cell-mono cell-strong">{a.sku}</span> },
      { key: "barcode", header: "Barcode", cell: (a) => <span className="cell-mono">{a.barcode}</span> },
      { key: "primary", header: "Primary", cell: (a) => (a.isPrimary ? <Badge>primary</Badge> : <span className="muted">—</span>) },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      {
        key: "actions",
        header: "",
        cell: (a) => (
          <span className="row-actions">
            <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${a.sku}`} onClick={() => setEditing(a)} />
            <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Delete ${a.sku}`} disabled={busy} onClick={() => void remove(a)} />
          </span>
        ),
      },
    ];
  }, [isAdmin, busy, remove]);

  return (
    <CrudPane
      isAdmin={isAdmin}
      q={q}
      onSearch={(v) => {
        setPage(1);
        setQ(v);
      }}
      placeholder="Search SKU or barcode"
      total={data?.total ?? 0}
      page={page}
      limit={limit}
      onPage={setPage}
      onAdd={() => setEditing("new")}
      onImport={() => setImporting(true)}
      addLabel="Add alias"
      loading={loading && !data}
      error={error}
      onRetry={load}
      empty={(data?.aliases.length ?? 0) === 0}
      emptyTitle="No barcode aliases"
      table={<DataTable ariaLabel="Barcode aliases" rows={data?.aliases ?? []} getRowKey={(a) => a.id} columns={columns} />}
    >
      {editing !== null && <AliasEditor alias={editing === "new" ? null : editing} toast={toast} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />}
      {importing && (
        <ImportDrawer
          title="Import barcode aliases"
          help="CSV columns: sku, barcode, isPrimary (optional: 1/true/yes)."
          toast={toast}
          parseRows={(text) => parseCsv(text).map((r) => ({ sku: r.sku ?? "", barcode: r.barcode ?? "", isPrimary: csvBool(r.isprimary) })).filter((r) => r.sku && r.barcode)}
          run={(rows) => api.admin.importSkuBarcodes(rows as SkuBarcodeInput[])}
          onClose={() => setImporting(false)}
          onDone={() => void load()}
        />
      )}
    </CrudPane>
  );
}

function AliasEditor({ alias, toast, onClose, onSaved }: { alias: AdminSkuBarcode | null; toast: Toast; onClose: () => void; onSaved: () => void }) {
  const isNew = alias === null;
  const [sku, setSku] = useState(alias?.sku ?? "");
  const [barcode, setBarcode] = useState(alias?.barcode ?? "");
  const [isPrimary, setIsPrimary] = useState(alias?.isPrimary ?? false);
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!sku.trim() || !barcode.trim()) return toast("SKU and barcode are required.", "danger");
    const input: SkuBarcodeInput = { sku: sku.trim(), barcode: barcode.trim(), isPrimary };
    setSaving(true);
    try {
      if (isNew) await api.admin.createSkuBarcode(input);
      else await api.admin.updateSkuBarcode(alias.id, input, alias.updatedAt);
      toast(isNew ? "Alias added" : "Alias saved", "success");
      onSaved();
    } catch (e) {
      toast(e instanceof ApiError && e.status === 409 ? "That SKU + barcode already exists." : "Couldn’t save the alias.", "danger");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Drawer onClose={onClose} ariaLabel={isNew ? "New alias" : "Edit alias"} title={isNew ? "Add barcode alias" : "Edit barcode alias"}>
      <div className="rule-form">
        <Field label="SKU"><Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="AN_SIER_NF_M" autoFocus /></Field>
        <Field label="Barcode"><Input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="92529230" /></Field>
        <Field label="Primary"><label className="rule-form__toggle"><input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} /><span>{isPrimary ? "Primary barcode" : "Secondary"}</span></label></Field>
        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : isNew ? "Add" : "Save"}</Button>
        </div>
      </div>
    </Drawer>
  );
}

/* ----------------------------- Bins ----------------------------- */
function BinsPane({ isAdmin, toast }: { isAdmin: boolean; toast: Toast }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ locations: AdminSkuLocation[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState<AdminSkuLocation | null | "new">(null);
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState(false);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setData(await api.admin.skuLocations({ q, page, limit }));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [q, page]);
  useEffect(() => {
    void load();
  }, [load]);

  const remove = useCallback(
    async (l: AdminSkuLocation) => {
      if (!window.confirm(`Delete bin for ${l.sku}?`)) return;
      setBusy(true);
      try {
        await api.admin.deleteSkuLocation(l.id);
        toast("Bin deleted", "success");
        void load();
      } catch {
        toast("Couldn’t delete.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [load, toast],
  );

  const columns = useMemo<Array<Column<AdminSkuLocation>>>(() => {
    const base: Array<Column<AdminSkuLocation>> = [
      { key: "sku", header: "SKU", cell: (l) => <span className="cell-mono cell-strong">{l.sku}</span> },
      { key: "primary", header: "Primary bin", cell: (l) => <span className="cell-mono">{l.primaryLocation}</span> },
      { key: "secondary", header: "Secondary", cell: (l) => <span className="cell-mono">{l.secondaryLocation ?? "—"}</span> },
    ];
    if (!isAdmin) return base;
    return [
      ...base,
      {
        key: "actions",
        header: "",
        cell: (l) => (
          <span className="row-actions">
            <Button variant="ghost" size="sm" icon="edit" iconOnly aria-label={`Edit ${l.sku}`} onClick={() => setEditing(l)} />
            <Button variant="ghost" size="sm" icon="x" iconOnly aria-label={`Delete ${l.sku}`} disabled={busy} onClick={() => void remove(l)} />
          </span>
        ),
      },
    ];
  }, [isAdmin, busy, remove]);

  return (
    <CrudPane
      isAdmin={isAdmin}
      q={q}
      onSearch={(v) => {
        setPage(1);
        setQ(v);
      }}
      placeholder="Search SKU or bin"
      total={data?.total ?? 0}
      page={page}
      limit={limit}
      onPage={setPage}
      onAdd={() => setEditing("new")}
      onImport={() => setImporting(true)}
      addLabel="Add bin"
      loading={loading && !data}
      error={error}
      onRetry={load}
      empty={(data?.locations.length ?? 0) === 0}
      emptyTitle="No bin locations"
      table={<DataTable ariaLabel="Bin locations" rows={data?.locations ?? []} getRowKey={(l) => l.id} columns={columns} />}
    >
      {editing !== null && <BinEditor location={editing === "new" ? null : editing} toast={toast} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />}
      {importing && (
        <ImportDrawer
          title="Import bin locations"
          help="CSV columns: sku, primaryLocation, secondaryLocation (optional)."
          toast={toast}
          parseRows={(text) => parseCsv(text).map((r) => ({ sku: r.sku ?? "", primaryLocation: r.primarylocation ?? r.primary ?? "", secondaryLocation: r.secondarylocation ?? r.secondary ?? null })).filter((r) => r.sku && r.primaryLocation)}
          run={(rows) => api.admin.importSkuLocations(rows as SkuLocationInput[])}
          onClose={() => setImporting(false)}
          onDone={() => void load()}
        />
      )}
    </CrudPane>
  );
}

function BinEditor({ location, toast, onClose, onSaved }: { location: AdminSkuLocation | null; toast: Toast; onClose: () => void; onSaved: () => void }) {
  const isNew = location === null;
  const [sku, setSku] = useState(location?.sku ?? "");
  const [primary, setPrimary] = useState(location?.primaryLocation ?? "");
  const [secondary, setSecondary] = useState(location?.secondaryLocation ?? "");
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!sku.trim() || !primary.trim()) return toast("SKU and primary bin are required.", "danger");
    const input: SkuLocationInput = { sku: sku.trim(), primaryLocation: primary.trim(), secondaryLocation: secondary.trim() || null };
    setSaving(true);
    try {
      if (isNew) await api.admin.createSkuLocation(input);
      else await api.admin.updateSkuLocation(location.id, input, location.updatedAt);
      toast(isNew ? "Bin added" : "Bin saved", "success");
      onSaved();
    } catch (e) {
      toast(e instanceof ApiError && e.status === 409 ? "That SKU already has a location." : "Couldn’t save the bin.", "danger");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Drawer onClose={onClose} ariaLabel={isNew ? "New bin" : "Edit bin"} title={isNew ? "Add bin location" : "Edit bin location"}>
      <div className="rule-form">
        <Field label="SKU"><Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="AN_SIER_NF_M" autoFocus /></Field>
        <Field label="Primary bin"><Input value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="A-12-3" /></Field>
        <Field label="Secondary bin (optional)"><Input value={secondary} onChange={(e) => setSecondary(e.target.value)} placeholder="B-04-1" /></Field>
        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : isNew ? "Add" : "Save"}</Button>
        </div>
      </div>
    </Drawer>
  );
}

/* ----------------------------- Shared pane shell + import ----------------------------- */
function CrudPane({
  isAdmin,
  q,
  onSearch,
  placeholder,
  total,
  page,
  limit,
  onPage,
  onAdd,
  onImport,
  addLabel,
  loading,
  error,
  onRetry,
  empty,
  emptyTitle,
  table,
  children,
}: {
  isAdmin: boolean;
  q: string;
  onSearch: (v: string) => void;
  placeholder: string;
  total: number;
  page: number;
  limit: number;
  onPage: (p: number) => void;
  onAdd: () => void;
  onImport: () => void;
  addLabel: string;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  empty: boolean;
  emptyTitle: string;
  table: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <>
      <div className="toolbar">
        <SearchBar
          className="toolbar__search"
          ariaLabel={placeholder}
          placeholder={placeholder}
          focusShortcut="/"
          value={q}
          onChange={onSearch}
        />
        {isAdmin && (
          <div className="toolbar__actions">
            <Button variant="secondary" size="sm" icon="plus" onClick={onImport}>Import CSV</Button>
            <Button variant="primary" size="sm" icon="plus" onClick={onAdd}>{addLabel}</Button>
          </div>
        )}
      </div>
      {error ? (
        <ErrorState title="Couldn’t load this" hint="Retry in a moment." onRetry={onRetry} />
      ) : loading ? (
        <div className="table-card"><SkeletonRows rows={6} cols={isAdmin ? 4 : 3} /></div>
      ) : empty ? (
        <EmptyState icon="box" title={emptyTitle} hint={isAdmin ? "Add one or import a CSV." : undefined} />
      ) : (
        <>
          {table}
          <footer className="pager">
            <Button variant="secondary" size="sm" icon="chevronLeft" disabled={page <= 1} onClick={() => onPage(Math.max(1, page - 1))}>Prev</Button>
            <span className="pager__info">Page {page} · {total} total</span>
            <Button variant="secondary" size="sm" disabled={page * limit >= total} onClick={() => onPage(page + 1)}>Next</Button>
          </footer>
        </>
      )}
      {children}
    </>
  );
}

function ImportDrawer<T>({
  title,
  help,
  toast,
  parseRows,
  run,
  onClose,
  onDone,
}: {
  title: string;
  help: string;
  toast: Toast;
  parseRows: (text: string) => T[];
  run: (rows: T[]) => Promise<{ results: ImportResult[]; summary: ImportSummary }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const parsed = useMemo(() => (text.trim() ? parseRows(text) : []), [text, parseRows]);

  async function go() {
    if (parsed.length === 0) return toast("No valid rows parsed.", "danger");
    setRunning(true);
    try {
      const res = await run(parsed);
      setSummary(res.summary);
      toast(`Imported ${res.summary.upserted}/${res.summary.total}${res.summary.errors ? ` · ${res.summary.errors} errors` : ""}`, res.summary.errors ? "warning" : "success");
      onDone();
    } catch {
      toast("Import failed.", "danger");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Drawer onClose={onClose} ariaLabel={title} title={title} meta={<span className="muted">{help}</span>}>
      <div className="rule-form">
        <Field label="Paste CSV (with a header row)">
          <textarea className="cond__json control" rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="sku,barcode,isPrimary&#10;AN_SIER_NF_M,92529230,1" spellCheck={false} aria-label="CSV" />
        </Field>
        <p className="muted">{parsed.length} valid row{parsed.length === 1 ? "" : "s"} detected.</p>
        {summary && <p className="muted">Last run: {summary.upserted} upserted, {summary.errors} errors of {summary.total}.</p>}
        <div className="rule-form__actions">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button variant="primary" icon="check" onClick={() => void go()} disabled={running || parsed.length === 0}>{running ? "Importing…" : `Import ${parsed.length}`}</Button>
        </div>
      </div>
    </Drawer>
  );
}
